// src/features/team/RosterPanel.tsx

import { useTeamStore, TEAM_SIZE } from '../../store/useTeamStore';
import { useScenarioStore } from '../../store/useScenarioStore';
import { useUiStore } from '../../store/useUiStore';
import { SPIRIT_MAP } from '../../data/mock-spirits';
import { NATURE_MAP } from '../../data/mock-natures';
import { SKILL_MAP } from '../../data/mock-skills';

export function RosterPanel() {
  const team = useTeamStore((s) => s.team);
  const attackerIndex = useScenarioStore((s) => s.attackerIndex);
  const defenderIndex = useScenarioStore((s) => s.defenderIndex);
  const setAttacker = useScenarioStore((s) => s.setAttacker);
  const setDefender = useScenarioStore((s) => s.setDefender);

  const openSpiritPicker = useUiStore((s) => s.openSpiritPicker);
  const openStatEditor = useUiStore((s) => s.openStatEditor);
  const openNatureEditor = useUiStore((s) => s.openNatureEditor);
  const openSkillPicker = useUiStore((s) => s.openSkillPicker);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: TEAM_SIZE }).map((_, i) => {
          const slot = team.slots[i];
          const spirit = slot?.spiritId ? SPIRIT_MAP[slot.spiritId] : null;
          const isAttacker = i === attackerIndex;
          const isDefender = i === defenderIndex;

          return (
            <div
              key={i}
              className={`p-4 rounded-xl border bg-slate-800 space-y-3 transition ${
                isAttacker
                  ? 'border-red-500/60'
                  : isDefender
                    ? 'border-blue-500/60'
                    : 'border-slate-700'
              }`}
            >
              {/* 槽位标题 */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500 font-mono">
                  槽位 {i + 1}
                </span>
                <div className="flex gap-1">
                  {isAttacker && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/40">
                      攻
                    </span>
                  )}
                  {isDefender && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/40">
                      守
                    </span>
                  )}
                </div>
              </div>

              {/* 精灵 */}
              <button
                onClick={() => openSpiritPicker(i)}
                className="w-full text-left px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 hover:border-slate-500 transition"
              >
                <p className="text-white font-semibold truncate">
                  {spirit?.name ?? '空槽位'}
                </p>
                {spirit && (
                  <p className="text-[10px] text-slate-500 mt-0.5 truncate">
                    {spirit.types.join(' / ')}
                  </p>
                )}
              </button>

              {/* 性格 / 等级 */}
              {slot?.spiritId && (
                <button
                  onClick={() => openNatureEditor(i)}
                  className="w-full text-xs px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-700 hover:border-slate-500 transition flex justify-between"
                >
                  <span className="text-slate-400">性格</span>
                  <span className="text-slate-200 font-mono">
                    {NATURE_MAP[slot.nature]?.name ?? '—'} · Lv.{slot.level}
                  </span>
                </button>
              )}

              {/* 技能数量 */}
              {slot?.spiritId && (
                <button
                  onClick={() => openSkillPicker(i)}
                  className="w-full text-xs px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-700 hover:border-slate-500 transition flex justify-between"
                >
                  <span className="text-slate-400">技能</span>
                  <span className="text-slate-200 font-mono">
                    {slot.skills.length} / 4
                  </span>
                </button>
              )}

              {/* 属性 */}
              {slot?.spiritId && (
                <button
                  onClick={() => openStatEditor(i)}
                  className="w-full text-xs py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 transition"
                >
                  种族值 / 个体值
                </button>
              )}

              {/* 设为攻击方 / 防守方 */}
              {slot?.spiritId && (
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => setAttacker(i)}
                    disabled={isAttacker}
                    className={`flex-1 text-[10px] py-1 rounded transition ${
                      isAttacker
                        ? 'bg-red-500/20 text-red-400 cursor-default'
                        : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                    }`}
                  >
                    设为攻击方
                  </button>
                  <button
                    onClick={() => setDefender(i)}
                    disabled={isDefender}
                    className={`flex-1 text-[10px] py-1 rounded transition ${
                      isDefender
                        ? 'bg-blue-500/20 text-blue-400 cursor-default'
                        : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                    }`}
                  >
                    设为防守方
                  </button>
                </div>
              )}

              {/* 技能名列表 */}
              {slot?.spiritId && slot.skills.length > 0 && (
                <ul className="text-[10px] text-slate-500 space-y-0.5 pt-1 border-t border-slate-700">
                  {slot.skills.map((sid, idx) => (
                    <li key={idx} className="truncate">
                      · {SKILL_MAP[sid]?.name ?? sid}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}